import { computeDebris, BURST_SPARKS, BURST_MOTES } from '../../revealSequence'
import type { RevealBurst } from './useRevealCascade'

interface RevealEffectsLayerProps {
  bursts: RevealBurst[]
  ref: React.RefObject<HTMLDivElement | null>
}

function burstStyle(burst: RevealBurst): React.CSSProperties {
  return {
    left: burst.cx,
    top: burst.cy,
    '--burst-size': `${burst.size}px`,
  } as React.CSSProperties
}

function debrisStyle(piece: ReturnType<typeof computeDebris>[number]): React.CSSProperties {
  return {
    '--tx': `${piece.tx}px`,
    '--ty': `${piece.ty}px`,
    '--g': `${piece.gravity}px`,
    '--ps': `${piece.size}px`,
    '--pd': `${piece.delay}ms`,
    '--pdur': `${piece.duration}ms`,
    '--rot': `${piece.angle}deg`,
  } as React.CSSProperties
}

/** Grid-level reveal effects — the bloom, the lens flare, the shock rings and the debris.
 *  Absolutely positioned inside the (untransformed) grid so they radiate over the whole
 *  grid, unclipped. Extracted from GameweekGrid.tsx by ticket 0122; the spotlight scrim it
 *  used to own was removed by ticket 0418, which lights the neighbouring cards instead of
 *  darkening them.
 *
 *  Two layers, because the flare has to sit BEHIND the exact card: light escaping around
 *  its edges reads as a light source, and the scoreline stays readable through the
 *  detonation instead of being washed out by it.
 */
function RevealEffectsLayer({ bursts, ref }: RevealEffectsLayerProps) {
  return (
    <>
      <div className="gameweek-grid__reveal-layer gameweek-grid__reveal-layer--under" aria-hidden="true">
        {bursts.map((burst) => (
          <div key={burst.key} className="gw-reveal-burst" style={burstStyle(burst)}>
            <span className="gw-reveal-flare">
              <span className="gw-reveal-flare__core" />
              <span className="gw-reveal-flare__bar gw-reveal-flare__bar--h" />
              <span className="gw-reveal-flare__bar gw-reveal-flare__bar--v" />
              <span className="gw-reveal-flare__bar gw-reveal-flare__bar--d1" />
              <span className="gw-reveal-flare__bar gw-reveal-flare__bar--d2" />
            </span>
          </div>
        ))}
      </div>
      <div ref={ref} className="gameweek-grid__reveal-layer" aria-hidden="true">
        {bursts.map((burst) => (
          <div
            key={burst.key}
            className={`gw-reveal-burst ${burst.volley ? 'gw-reveal-burst--volley' : ''}`}
            style={burstStyle(burst)}
          >
            {/* Lights the fixtures around the exact card rather than dimming them —
                the whole point of ticket 0418's rework. */}
            <span className="gw-reveal-bloom" />
            <span className="gw-reveal-shockwave gw-reveal-shockwave--soft" />
            <span className="gw-reveal-shockwave" />
            {/* Two kinds at once: big soft motes behind, fast sharp sparks in front.
                Either on its own reads flat however it is tuned. */}
            {computeDebris(burst.volley ? BURST_MOTES / 2 : BURST_MOTES, 'mote').map((p, i) => (
              <span
                key={`m${i}`}
                className={`gw-debris gw-debris--mote gw-debris--c${p.colour}`}
                style={debrisStyle(p)}
              />
            ))}
            {computeDebris(burst.volley ? BURST_SPARKS / 2 : BURST_SPARKS, 'spark').map((p, i) => (
              <span
                key={`s${i}`}
                className={`gw-debris gw-debris--spark gw-debris--c${p.colour}`}
                style={debrisStyle(p)}
              />
            ))}
          </div>
        ))}
      </div>
    </>
  )
}

export default RevealEffectsLayer
